import moment from "moment-timezone";
import type {
  BarDisplay,
  CommodityId,
  Instrument,
  ResolvedInstrument,
  ResolvedWindow,
  DataSeries,
} from "@/lib/api/assistant";

export const COMMODITY_LABELS: Record<CommodityId, string> = {
  C: "Corn",
  S: "Soybeans",
  MW: "Minneapolis Wheat",
  CNL: "Canola",
};

export function commodityLabel(id: CommodityId): string {
  return COMMODITY_LABELS[id] ?? id;
}

export function dataSeriesLabel(ds: DataSeries): string {
  if (ds.kind === "price_component") return ds.value;
  if (ds.kind === "computed_output") return "Price Signal";
  return `${ds.value.toUpperCase()} (study)`;
}

export function instrumentLabel(instrument: Instrument | ResolvedInstrument): string {
  const base = instrument.base ? commodityLabel(instrument.base as CommodityId) : "—";
  const contract = instrument.option_month
    ? instrument.option_month
    : instrument.template === "continuous"
      ? "Continuous"
      : instrument.template === "new_crop"
        ? "New Crop"
        : "—";
  const series = dataSeriesLabel(instrument.data_series);
  return `${base} · ${contract} · ${series} · ${instrument.bar_frequency}`;
}

// Assistant timestamps are always UTC-anchored. We display them in UTC so
// that "Jan 1 2025" (midnight UTC) renders as Jan 1 — not Dec 31 in local
// timezones that are behind UTC.
export function formatTs(ts: number): string {
  return moment.utc(ts).format("MMM D, YYYY");
}

export function formatTsTime(ts: number): string {
  return moment.utc(ts).format("MMM D, YYYY HH:mm");
}

/**
 * Format a bar timestamp using the backend-supplied display hint.
 * Never derives the format from bar_frequency on the client side.
 */
export function formatBarTs(ts: number, barDisplay: BarDisplay): string {
  return barDisplay === "date_only"
    ? moment.utc(ts).format("MMM D, YYYY")
    : moment.utc(ts).format("MMM D, YYYY HH:mm");
}

export function formatWindow(window: ResolvedWindow): string {
  return `${formatTs(window.start_ts)} – ${formatTs(window.end_ts)}`;
}

/** Format a decimal.js string as a number with up to 4 fractional digits. */
export function formatNum(value: string | number, fractionDigits = 4): string {
  const n = typeof value === "string" ? Number(value) : value;
  if (!Number.isFinite(n)) return String(value);
  // trim trailing zeros, but cap precision
  const fixed = n.toFixed(fractionDigits);
  return fixed.replace(/\.?0+$/, "");
}

export function formatPct(value: string | number, fractionDigits = 2): string {
  return `${formatNum(value, fractionDigits)}%`;
}

export function humanizeKey(key: string): string {
  return key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
